import {
  GenericTable,
  GenericTableVariant,
  Modal,
  NewButton,
  NewButtonVariant,
  Spinner,
} from "@visiblethread/react-components";
import Flex, { GAP_CLASS } from "components/Flex";
import { modalTypes } from "constants/types";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { selectFilters } from "slices/captureDashboard";
import {
  exportPipelineByOwnerCsv,
  exportPipelineByOwnerPdf,
} from "slices/pipelineByOwner/actions";
import {
  fetchPipelineByOwnerRanked,
  resetPagination,
  selectPipelineByOwnerRankedData,
  selectPipelineByOwnerRankedError,
  selectPipelineByOwnerRankedLoading,
  selectPipelineByOwnerRankedOffset,
  selectPipelineByOwnerRankedPageSize,
  setOffset,
} from "slices/pipelineByOwnerRanked";

import { createColumns } from "./columns";
import styles from "./PipelineByOwnerModal.module.scss";

const MODAL_TITLE = "Pipeline by Owner";
const EMPTY_MSG = "No assignees in this period.";
const SUBHEADER_SUFFIX = "by total value of assigned opportunities";

const assigneesLabel = (n) => (n === 1 ? "1 assignee" : `${n} assignees`);

const PipelineByOwnerModal = () => {
  const dispatch = useDispatch();
  const filters = useSelector(selectFilters);
  const data = useSelector(selectPipelineByOwnerRankedData);
  const isLoading = useSelector(selectPipelineByOwnerRankedLoading);
  const error = useSelector(selectPipelineByOwnerRankedError);
  const offset = useSelector(selectPipelineByOwnerRankedOffset);
  const pageSize = useSelector(selectPipelineByOwnerRankedPageSize);

  useEffect(() => {
    dispatch(resetPagination());
    dispatch(fetchPipelineByOwnerRanked());
  }, [dispatch, filters]);

  const onDismiss = () => {
    dispatch(hideModal(modalTypes.PIPELINE_BY_OWNER));
  };

  const totalCount = data?.totalCount ?? 0;
  const currency = data?.currency ?? "$";
  const owners = data?.owners ?? [];
  const currentPage = Math.floor(offset / pageSize) + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const onPrevPage = () => {
    dispatch(setOffset(Math.max(0, offset - pageSize)));
    dispatch(fetchPipelineByOwnerRanked());
  };

  const onNextPage = () => {
    dispatch(setOffset(offset + pageSize));
    dispatch(fetchPipelineByOwnerRanked());
  };

  const columns = useMemo(() => createColumns(currency), [currency]);

  return (
    <Modal
      backdrop
      centered
      className={`fade-in ${styles.modal}`}
      onDismiss={onDismiss}
      show
      size="lg"
    >
      <Modal.Header>{MODAL_TITLE}</Modal.Header>
      <Modal.Body>
        <div className={styles.pipelineByOwnerModal}>
          {error && (
            <div className={styles.pipelineByOwnerModal__error} role="alert">
              {error}
            </div>
          )}
          {!error && (
            <div className={styles.pipelineByOwnerModal__subHeaderRow}>
              <p className={styles.pipelineByOwnerModal__subHeader}>
                {assigneesLabel(totalCount)} · {SUBHEADER_SUFFIX}
              </p>
              {isLoading && <Spinner />}
            </div>
          )}
          {!error && totalCount === 0 && !isLoading && (
            <p className={styles.pipelineByOwnerModal__empty}>{EMPTY_MSG}</p>
          )}
          {!error && owners.length > 0 && (
            <div className={styles.pipelineByOwnerModal__tableWrapper}>
              <GenericTable
                columns={columns}
                data={owners}
                variant={GenericTableVariant.bordered}
              />
            </div>
          )}
          {totalPages > 1 && (
            <div className={styles.pipelineByOwnerModal__pagination}>
              <NewButton
                disabled={offset === 0}
                onClick={onPrevPage}
                variant={NewButtonVariant.l2}
              >
                Previous
              </NewButton>
              <span className={styles.pipelineByOwnerModal__pageLabel}>
                Page {currentPage} of {totalPages}
              </span>
              <NewButton
                disabled={offset + pageSize >= totalCount}
                onClick={onNextPage}
                variant={NewButtonVariant.l2}
              >
                Next
              </NewButton>
            </div>
          )}
          {!error && (
            <Flex justifyContent="flex-end" gap={GAP_CLASS.GAP_200}>
              <NewButton
                disabled={totalCount === 0}
                onClick={() => dispatch(exportPipelineByOwnerCsv())}
                variant={NewButtonVariant.l2}
              >
                Export CSV
              </NewButton>
              <NewButton
                disabled={totalCount === 0}
                onClick={() => dispatch(exportPipelineByOwnerPdf())}
                variant={NewButtonVariant.l1}
              >
                Export PDF
              </NewButton>
            </Flex>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PipelineByOwnerModal;
